import {
  calculateDatabaseProjectionAggregate,
  type DatabaseProjectionAggregateResult,
  type DatabaseProjectionColumn,
  type DatabaseProjectionDiagnostic,
  type DatabaseProjectionGroupSummary,
  type DatabaseProjectionPage,
  type DatabaseProjectionPredicate,
  type DatabaseProjectionSort,
  matchesDatabaseProjectionPredicate,
  normalizeDatabaseProjectionWindow,
  sortDatabaseRowsAsView,
} from '@streamlain/database-identity';
export type {
  DatabaseProjectionAggregateRequest,
  DatabaseProjectionAggregateResult,
  DatabaseProjectionAggregateType,
  DatabaseProjectionColumnPredicate,
  DatabaseProjectionDiagnostic,
  DatabaseProjectionPredicate,
  DatabaseProjectionPredicateGroup,
  DatabaseProjectionSort,
  DatabaseProjectionTemporalOverlapPredicate,
  DatabaseProjectionTextPredicate,
  DatabaseProjectionWindow,
} from '@streamlain/database-identity';
import type {
  DatabaseProjectionFreshness,
  DatabaseProjectionQuery,
  DatabaseProjectionResultSource,
  DatabaseProjectionSyncState,
} from '@streamlain/database-identity';

import { evalFilter } from '../filter/eval.js';
import { filterMatcher } from '../filter/filter-fn/matcher.js';
import type { Filter, FilterGroup } from '../filter/types.js';
import type { TypeInstance } from '../logical/type.js';
import { projectionValueType } from '../sort/eval.js';
export type {
  DatabaseProjectionFreshness,
  DatabaseProjectionQuery,
  DatabaseProjectionResultSource,
  DatabaseProjectionRowScope,
  DatabaseProjectionSyncState,
} from '@streamlain/database-identity';

export type DatabaseProjectionResult = {
  /** Internal summary channel; no live full-row grouping in sharded views. */
  groups?: readonly DatabaseProjectionGroupSummary[];
  aggregates: readonly DatabaseProjectionAggregateResult[];
  complete: boolean;
  diagnostics: readonly DatabaseProjectionDiagnostic[];
  freshness: DatabaseProjectionFreshness;
  query: DatabaseProjectionQuery;
  rowIds: readonly string[];
  source: DatabaseProjectionResultSource;
  syncState: DatabaseProjectionSyncState;
  totalCount: number;
  totalKnown: boolean;
  window: {
    limit: number;
    offset: number;
  };
};

export type DatabaseProjectionSubscriptionResult = DatabaseProjectionResult & {
  groups: readonly DatabaseProjectionGroupSummary[];
};

export function toFrontendProjectionResult(
  page: DatabaseProjectionPage,
  query: DatabaseProjectionQuery
): DatabaseProjectionResult {
  const {
    aggregates,
    complete,
    diagnostics,
    freshness,
    rowIds,
    source,
    syncState,
    totalCount,
    totalKnown,
    window,
  } = page;
  return {
    aggregates,
    complete,
    diagnostics,
    freshness,
    query,
    rowIds,
    source,
    syncState,
    totalCount,
    totalKnown,
    window,
  };
}

export type DatabaseProjectionSource = Pick<
  DatabaseProjectionResult,
  'complete' | 'diagnostics' | 'freshness' | 'rowIds' | 'source' | 'syncState'
>;

export type DatabaseProjectionExecutionInput = {
  filterContext?: { nowMs: number };
  /** The columns of `propertyIds`: a sort compares each by its type, as the view does. */
  columns: readonly DatabaseProjectionColumn[];
  complete: boolean;
  freshness: DatabaseProjectionFreshness;
  propertyIds: readonly string[];
  query: DatabaseProjectionQuery;
  readCellValue: (rowId: string, propertyId: string) => unknown;
  rowIds: readonly string[];
  rowPredicate?: (rowId: string) => boolean;
  source: DatabaseProjectionResultSource;
  syncState?: DatabaseProjectionSyncState;
};

function collectUnsupportedFilterDiagnostics(
  filter: Filter | undefined,
  diagnostics: DatabaseProjectionDiagnostic[],
  path = 'filter'
): boolean {
  if (!filter) return true;
  if (filter.type === 'group') {
    return filter.conditions.every((condition, index) =>
      collectUnsupportedFilterDiagnostics(
        condition,
        diagnostics,
        `${path}.conditions.${index}`
      )
    );
  }
  if (!filterMatcher.getFilterByName(filter.function)) {
    diagnostics.push({
      code: 'unsupported-operator',
      message: `Unsupported projection filter operator "${filter.function ?? ''}".`,
      path: `${path}.function`,
    });
    return false;
  }
  return true;
}

function rowMatchesFilter({
  context,
  filter,
  propertyIds,
  readCellValue,
  rowId,
}: {
  context: { nowMs: number };
  filter: FilterGroup;
  propertyIds: readonly string[];
  readCellValue: DatabaseProjectionExecutionInput['readCellValue'];
  rowId: string;
}): boolean {
  if (!filter.conditions.length) return true;
  const row = Object.fromEntries(
    propertyIds.map(propertyId => [
      propertyId,
      readCellValue(rowId, propertyId),
    ])
  );
  return evalFilter(filter, row, context);
}

function collectUnsupportedAggregateDiagnostics(
  query: DatabaseProjectionQuery,
  diagnostics: DatabaseProjectionDiagnostic[]
): void {
  query.aggregates?.forEach((aggregate, index) => {
    if (aggregate.type === 'count' || aggregate.propertyId) {
      return;
    }
    diagnostics.push({
      code: 'unsupported-aggregate',
      message: `Projection aggregate "${aggregate.type}" requires a propertyId.`,
      path: `aggregates.${index}.propertyId`,
    });
  });
  if (query.groupBy && query.aggregates?.length) {
    diagnostics.push({
      code: 'unsupported-aggregate',
      message:
        'Grouped projection aggregates require a grouped aggregate executor.',
      path: 'groupBy',
    });
  }
}

function getProjectionSorts(
  query: DatabaseProjectionQuery
): DatabaseProjectionSort[] {
  return [
    ...(query.viewSort?.sortBy.map(sort => ({
      direction: sort.desc ? ('desc' as const) : ('asc' as const),
      propertyId: sort.ref.name,
      type: 'property' as const,
    })) ?? []),
    ...(query.sort ?? []),
  ];
}

function collectFilterPropertyIds(
  filter: Filter | undefined,
  ids: Set<string>
) {
  if (!filter) return;
  if (filter.type === 'group') {
    filter.conditions.forEach(condition =>
      collectFilterPropertyIds(condition, ids)
    );
    return;
  }
  if (filter.left.type === 'ref') {
    ids.add(filter.left.name);
  }
}

function collectPredicatePropertyIds(
  predicate: DatabaseProjectionPredicate,
  ids: Set<string>
) {
  if (predicate.type === 'column') {
    ids.add(predicate.propertyId);
  } else if (predicate.type === 'temporal-overlap') {
    ids.add(predicate.startPropertyId);
    if (predicate.endPropertyId) ids.add(predicate.endPropertyId);
  } else if (predicate.type === 'text-search') {
    predicate.propertyIds?.forEach(propertyId => ids.add(propertyId));
  } else {
    predicate.predicates.forEach(child =>
      collectPredicatePropertyIds(child, ids)
    );
  }
}

/**
 * The projection columns of `propertyIds` of a data source, each with the
 * type its cells have in the table view (`propertyDataTypeGet`): a sort
 * compares them as the view does.
 */
export function dataSourceProjectionColumns(
  source: {
    propertyNameGet(propertyId: string): string;
    propertyTypeGet(propertyId: string): string | undefined;
    propertyDataGet(propertyId: string): Record<string, unknown>;
    propertyDataTypeGet(propertyId: string): TypeInstance | undefined;
  },
  propertyIds: readonly string[]
): DatabaseProjectionColumn[] {
  return propertyIds.flatMap(id => {
    const type = source.propertyTypeGet(id);
    if (!type) return [];
    const dataType = source.propertyDataTypeGet(id);
    const valueType = dataType && projectionValueType(dataType);
    return [
      {
        id,
        name: source.propertyNameGet(id),
        type,
        data: source.propertyDataGet(id),
        logicalType: valueType
          ? {
              name: valueType.name,
              data: valueType.data ?? null,
              ...(valueType.element
                ? { element: { name: valueType.element.name } }
                : {}),
            }
          : null,
      },
    ];
  });
}

export function getProjectionPropertyIds(
  query: DatabaseProjectionQuery,
  allPropertyIds: readonly string[]
): readonly string[] {
  if (
    query.searchText?.trim() ||
    query.predicates?.some(
      predicate =>
        predicate.type === 'text-search' && !predicate.propertyIds?.length
    )
  ) {
    return allPropertyIds;
  }
  const ids = new Set(query.requestedPropertyIds ?? []);
  collectFilterPropertyIds(query.filter, ids);
  query.predicates?.forEach(predicate =>
    collectPredicatePropertyIds(predicate, ids)
  );
  getProjectionSorts(query).forEach(sort => ids.add(sort.propertyId));
  query.aggregates?.forEach(aggregate => {
    if (aggregate.propertyId) ids.add(aggregate.propertyId);
  });
  return ids.size ? [...ids] : allPropertyIds;
}

function sortByManualOrder(
  rowIds: readonly string[],
  manuallySort: readonly string[] | undefined
): string[] {
  if (!manuallySort?.length) return rowIds.slice();
  const manualIndex = new Map(
    manuallySort.map((rowId, index) => [rowId, index])
  );
  return rowIds.slice().sort((left, right) => {
    const leftIndex = manualIndex.get(left);
    const rightIndex = manualIndex.get(right);
    if (leftIndex === undefined && rightIndex === undefined) return 0;
    if (leftIndex === undefined) return 1;
    if (rightIndex === undefined) return -1;
    return leftIndex - rightIndex;
  });
}

export function canReuseProjectionSourceRows(
  query: DatabaseProjectionQuery,
  hasRowPredicate: boolean
): boolean {
  return !(
    hasRowPredicate ||
    query.aggregates?.length ||
    query.filter?.conditions.length ||
    query.predicates?.length ||
    query.searchText?.trim() ||
    query.sort?.length ||
    query.viewSort?.manuallySort.length ||
    query.viewSort?.sortBy.length ||
    query.window
  );
}

export function canProjectSourceWindow(
  query: DatabaseProjectionQuery,
  hasRowPredicate: boolean
): boolean {
  return Boolean(
    query.window &&
    canReuseProjectionSourceRows(
      { ...query, window: undefined },
      hasRowPredicate
    )
  );
}

export function projectDatabaseRows(
  input: DatabaseProjectionExecutionInput
): DatabaseProjectionResult {
  if (canReuseProjectionSourceRows(input.query, Boolean(input.rowPredicate))) {
    const diagnostics: DatabaseProjectionDiagnostic[] = input.complete
      ? []
      : [
          {
            code: 'partial-source',
            message:
              'Projection source is partial; result must not be rendered as complete.',
          },
        ];
    return {
      aggregates: [],
      complete: input.complete,
      diagnostics,
      freshness: input.freshness,
      query: input.query,
      rowIds: input.rowIds,
      source: input.source,
      syncState: input.syncState ?? (input.complete ? 'live' : 'partial'),
      totalCount: input.rowIds.length,
      totalKnown: input.complete,
      window: { limit: input.rowIds.length, offset: 0 },
    };
  }
  if (canProjectSourceWindow(input.query, Boolean(input.rowPredicate))) {
    const diagnostics: DatabaseProjectionDiagnostic[] = [];
    const window = normalizeDatabaseProjectionWindow(
      input.query.window,
      input.rowIds.length,
      diagnostics
    );
    const valid = diagnostics.length === 0;
    if (!input.complete) {
      diagnostics.push({
        code: 'partial-source',
        message:
          'Projection source is partial; result must not be rendered as complete.',
      });
    }
    return {
      aggregates: [],
      complete: input.complete && valid,
      diagnostics,
      freshness: input.freshness,
      query: input.query,
      rowIds:
        window.limit === 0
          ? []
          : input.rowIds.slice(window.offset, window.offset + window.limit),
      source: input.source,
      syncState: valid
        ? (input.syncState ?? (input.complete ? 'live' : 'partial'))
        : 'unsupported',
      totalCount: input.rowIds.length,
      totalKnown: input.complete && valid,
      window,
    };
  }
  const diagnostics: DatabaseProjectionDiagnostic[] = [];
  const predicateDiagnostics = new Set<string>();
  const searchText = input.query.searchText?.trim();
  const predicates = [
    ...(searchText
      ? [{ query: searchText, type: 'text-search' } as const]
      : []),
    ...(input.query.predicates ?? []),
  ];
  const filterValid = collectUnsupportedFilterDiagnostics(
    input.query.filter,
    diagnostics
  );
  collectUnsupportedAggregateDiagnostics(input.query, diagnostics);
  const filterContext = input.filterContext ?? { nowMs: Date.now() };
  let filteredRowIds = input.rowIds.filter(rowId => {
    if (input.rowPredicate && !input.rowPredicate(rowId)) {
      return false;
    }
    if (
      input.query.filter &&
      (!filterValid ||
        !rowMatchesFilter({
          context: filterContext,
          filter: input.query.filter,
          propertyIds: input.propertyIds,
          readCellValue: input.readCellValue,
          rowId,
        }))
    ) {
      return false;
    }
    const previousDiagnosticCount = diagnostics.length;
    const matches = predicates.every(predicate =>
      matchesDatabaseProjectionPredicate(
        predicate,
        propertyId => input.readCellValue(rowId, propertyId),
        input.propertyIds,
        diagnostics
      )
    );
    for (const diagnostic of diagnostics.slice(previousDiagnosticCount)) {
      const key = `${diagnostic.code}:${diagnostic.path ?? ''}`;
      if (predicateDiagnostics.has(key)) {
        diagnostics.pop();
      } else {
        predicateDiagnostics.add(key);
      }
    }
    return matches;
  });

  filteredRowIds = sortByManualOrder(
    filteredRowIds,
    input.query.viewSort?.manuallySort
  );

  const sorts = getProjectionSorts(input.query).filter(sort => {
    if (sort.type === 'property') return true;
    diagnostics.push({
      code: 'unsupported-sort',
      message: `Unsupported projection sort "${String(sort.type)}".`,
      path: 'sort.type',
    });
    return false;
  });
  filteredRowIds = sortDatabaseRowsAsView(
    filteredRowIds,
    sorts,
    input.columns,
    input.readCellValue
  );

  const totalCount = filteredRowIds.length;
  const window = normalizeDatabaseProjectionWindow(
    input.query.window,
    totalCount,
    diagnostics
  );
  const rowIds =
    window.limit === 0
      ? []
      : filteredRowIds.slice(window.offset, window.offset + window.limit);
  const valid = diagnostics.length === 0;
  const complete = input.complete && valid;
  const syncState: DatabaseProjectionSyncState = valid
    ? (input.syncState ?? (input.complete ? 'live' : 'partial'))
    : 'unsupported';

  if (!input.complete) {
    diagnostics.push({
      code: 'partial-source',
      message:
        'Projection source is partial; result must not be rendered as complete.',
    });
  }

  return {
    aggregates: (input.query.aggregates ?? []).map(request =>
      calculateDatabaseProjectionAggregate(
        request,
        request.type === 'count' || !request.propertyId
          ? filteredRowIds
          : filteredRowIds.map(rowId =>
              input.readCellValue(rowId, request.propertyId!)
            ),
        complete
      )
    ),
    complete,
    diagnostics,
    freshness: input.freshness,
    query: input.query,
    rowIds,
    source: input.source,
    syncState,
    totalCount,
    totalKnown: complete,
    window,
  };
}
